import {
  Accordion, Alert, Button, Dropdown, IconLoading, IconSites, Link, Tag,
  Tooltip,
} from 'gatorcomponents';
import Cookies from 'js-cookie';
import React, { useCallback, useState } from 'react';
import * as Styles from './CardGroup.styles';
import { useMountCardStatus } from '@/hooks/sites/sitesList/useMountCardStatus';
import { STATUS_ALERTS_VARIANTS, STATUS_TYPE } from '@/enums/sites/status.enum';
import SiteAlert from '../../../SiteAlert';
import { PROPAGATION_STATUS } from '@/enums/domains/domains.enum';
import { useResize } from '@/hooks/useResize';
import withErrorBoundary from '@/utils/errorBoundary';

const CardGroup = ({
  group,
  propagationStatus,
  actions,
  getDropdownActions,
  getMainActions,
  loadMoreLabel,
  changeDomainLabel,
  handlePayInvoice,
  closerPayment,
  loading,
  siteTagLabels,
  propagationAlertLabels,
  hostCount,
  handleEditSite,
  loadingRedirect,
  editSiteCta,
  displayChangeDomain,
  temporaryDomains,
  handleGoToChangeDomain,
}) => {
  const statusOnCookie = Cookies.get(`${group[0].hosting_id}-accordion-status`);
  const accordionStatus = statusOnCookie ? statusOnCookie === 'true' : true;
  const [open, setOpen] = useState(accordionStatus);
  const itemsPerPage = 5;
  const [page, setPage] = useState(1);
  const { getSiteStatus } = useMountCardStatus();
  const mainSite = group[0];
  const planStatus = getSiteStatus(mainSite);
  const windowRect = useResize();
  const mobile = windowRect.width < 600;
  const [isLoading, setIsLoading] = useState(false);

  const handleChangeDomain = useCallback(async ({ domain, hostingId }) => {
    setIsLoading(true);
    await handleGoToChangeDomain && handleGoToChangeDomain({ domain, hostingId });
    setIsLoading(false);
  }, [handleGoToChangeDomain]);

  const shouldRenderLoadMore = useCallback(() => {
    const shoudRender = mainSite && mainSite.cpanel_domains && mainSite.cpanel_domains.length > (page * itemsPerPage);
    return shoudRender;
  }, [page, mainSite]);

  const handleClickEditSite = useCallback(({ hostingId, domain, builderAddonId }) => {
    handleEditSite && handleEditSite({
      hostingId,
      domain,
      builderAddonId,
    });
  }, [handleEditSite]);

  const mountAccordionItems = useCallback(group => (
    <Styles.ListWrapper data-testid={`listwrapper${group[0].hosting_id}`}>
      {group.map(item => item.cpanel_domains && item.cpanel_domains.slice(0, (itemsPerPage * page)).map((cpanelItem) => {
        const tagRules = {
          is_main_domain: <Tag label={siteTagLabels.is_main_domain} variant="info" testId={`mainDomainTag${item.hosting_id}`} />,
          site_offline: <Tag label={siteTagLabels.site_offline} variant="problem" />,
          isTemp: (
            <Tooltip content={siteTagLabels.isTempTooltip}>
              <Tag label={siteTagLabels.isTemp} variant="neutral" />
            </Tooltip>
          ),
        };

        const isMain = cpanelItem.domain === item.domain && cpanelItem.type === 'main';
        const isAddon = cpanelItem.type === 'addon';

        const isTemp = !!(displayChangeDomain && temporaryDomains.find(item => item.domain === cpanelItem.domain));

        const dropdownActions = getDropdownActions({ siteItem: item, grouped: true, cpanelItem });
        const mainActions = getMainActions({ siteItem: item, accordionItem: true });
        const propagationItem = propagationStatus.find(domainItem => domainItem.domain === item.site);
        const validStatus = [PROPAGATION_STATUS.IN_PROGRESS, PROPAGATION_STATUS.NOT_CONTRACTED];
        const isValidPropagationStatus = propagationItem && validStatus.includes(propagationItem.status);
        const siteIsOffline = ['cancelled', 'terminated', 'suspended'].includes(item.status.toLowerCase());

        const mountPropagationAlert = () => {
          if (!propagationItem) {
            return;
          }

          const alertRules = {
            [PROPAGATION_STATUS.IN_PROGRESS]: <Alert
              variant="timed"
              description={propagationAlertLabels.in_progress}
              withoutBackground
              centerIcon={!mobile}
            />,
            [PROPAGATION_STATUS.NOT_CONTRACTED]: <Alert
              variant="warning"
              description={propagationAlertLabels.not_contracted}
              customIcon="IconDanger"
              withoutBackground
              centerIcon={!mobile}
            />,
          };

          if (propagationItem && isValidPropagationStatus) {
            return alertRules[propagationItem.status];
          }
        };

        if (mobile) {
          if (displayChangeDomain && (isMain || isAddon)) {
            dropdownActions.list.push(
              {
                id: `item-${item.hosting_id}-changeDomain`,
                label: changeDomainLabel,
                icon: null,
                onClick: () => handleChangeDomain({
                  domain: cpanelItem.domain, hostingId: item.hosting_id,
                }),
                variant: 'secondary',
              },
            );
          }
          dropdownActions.list.push(
            {
              id: `item-${item.hosting_id}-editSite`,
              label: loadingRedirect === cpanelItem.domain ? <IconLoading /> : editSiteCta,
              icon: null,
              onClick: () => handleClickEditSite({ hostingId: group[0].hosting_id, domain: cpanelItem.domain, builderAddonId: item.builder_addon_id }),
              variant: 'secondary',
            },
          );
        }

        return (
          <Styles.CardItem key={`${item.hosting_id}-${cpanelItem.domain}`} id={`${item.hosting_id}-${cpanelItem.domain}`}>
            <IconSites />

            <Link
              text={cpanelItem.domain}
              href={`https://${cpanelItem.domain}`}
            />

            <Styles.TagsWrapper noDisplay={!isMain && !siteIsOffline}>
              {isMain && tagRules.is_main_domain}
              {isTemp && tagRules.isTemp}
              {siteIsOffline && tagRules.site_offline}
            </Styles.TagsWrapper>

            <Styles.MainActionsWrapper noMargin={isValidPropagationStatus} noDisplay={!mainActions.length}>
              {(displayChangeDomain && (isMain || isAddon)) && (
                <Button
                  variant="text"
                  label={changeDomainLabel}
                  onClick={() => handleChangeDomain({
                    domain: cpanelItem.domain, hostingId: item.hosting_id,
                  })}
                  loading={isLoading}
                  disabled={isLoading}
                />
              )}

              <Button
                key={`${item}_${cpanelItem.domain}_edit`}
                label={loadingRedirect === cpanelItem.domain ? <IconLoading /> : editSiteCta}
                variant="secondary"
                onClick={() => handleClickEditSite({ hostingId: group[0].hosting_id, domain: cpanelItem.domain, builderAddonId: item.builder_addon_id })}
                testId={`${item}_${cpanelItem.domain}_edit`}
                size="small"
                disabled={[STATUS_TYPE.ANALYSIS, STATUS_TYPE.PENDING].includes(planStatus) || loadingRedirect === cpanelItem.domain}
              />
            </Styles.MainActionsWrapper>

            {dropdownActions.list.length > 0 && (
              <Styles.DropdownButtonWrapper>
                <Dropdown
                  items={dropdownActions.list}
                />
              </Styles.DropdownButtonWrapper>
            )}

            <Styles.ItemAlert noDisplay={!isValidPropagationStatus}>
              {mountPropagationAlert()}
            </Styles.ItemAlert>
          </Styles.CardItem>
        );
      }))}

      {shouldRenderLoadMore() && (
        <Styles.ButtonWrapper>
          <Button
            variant="secondary"
            onClick={() => setPage(page + 1)}
            testId="loadMore"
            label={loadMoreLabel}
          />
        </Styles.ButtonWrapper>
      )}
    </Styles.ListWrapper>
  ), [
    page,
    shouldRenderLoadMore,
    getDropdownActions,
    getMainActions,
    loadMoreLabel,
    mobile,
    propagationAlertLabels,
    propagationStatus,
    siteTagLabels,
    planStatus,
    handleClickEditSite,
    editSiteCta,
    loadingRedirect,
    displayChangeDomain,
    changeDomainLabel,
    handleChangeDomain,
    temporaryDomains,
  ]);

  const handleToggle = (group) => {
    Cookies.set(`${group[0].hosting_id}-accordion-status`, !open);
    setOpen(!open);
  };

  const financialAlertProps = () => {
    const alertVariant = STATUS_ALERTS_VARIANTS.financial[planStatus];

    const defaultProps = {
      status: planStatus,
      type: 'financial',
      alertVariant,
      handleClick: handlePayInvoice,
      daysForPayment: closerPayment && closerPayment(planStatus, mainSite),
      newSitesAlert: true,
      groupMainDomain: group[0].domain,
      invoiceId: group[0].invoice.id,
    };

    return defaultProps;
  };

  const planNotification = () => {
    if (group[0].sub_status !== STATUS_TYPE.ACTIVE) {
      return (
        <Styles.AlertWrapper>
          <SiteAlert {...financialAlertProps()} />
        </Styles.AlertWrapper>
      );
    }
  };

  const shouldrender = group[0].product_name;

  return (
    shouldrender && (
      <Accordion
        key={`dropdown${group[0].hosting_id}`}
        testId={`dropdown${group[0].hosting_id}`}
        items={() => mountAccordionItems(group)}
        width="full"
        title={`${group[0].product_name}`}
        justifyContent="flex-start"
        subtitle={hostCount({ group })}
        actions={actions(group, planStatus)}
        open={planStatus === STATUS_TYPE.SUSPENDED ? false : open}
        onToggleAccordion={() => handleToggle(group)}
        leftIcon="IconFile"
        notification={planNotification()}
        loading={loading}
        allowToggle={planStatus !== STATUS_TYPE.SUSPENDED}
        actionsBreakpoint={1080}
      />
    )
  );
};

export default withErrorBoundary(CardGroup, 'CardGroup');
